// Visual print-area editor. Mockup is shown at a fixed display scale; the
// admin drags the rectangle to POSITION it. SIZING is locked by the
// location's name to an industry-standard preset so every garment ships
// with predictable print dimensions:
//
//   Front / Back            → 40 x 50 cm print (400 x 500 mm)
//   Left/Right Sleeve       → standard sleeve (80 x 250 mm long, 80 x 130 mm short)
//   Hat front / Hat side    → embroidery / DTF standard hat sizes
//   Left/Right Chest        → 90 x 90 mm pocket-print
//   Hem                     → 200 x 50 mm hem strip
//
// Clicking the print-area rectangle opens a floating toolbar that surfaces
// the industry-standards selector inline above the rect — no more separate
// dropdown at the top of the editor. The toolbar closes when the admin
// clicks anywhere else.
//
// Values are persisted in CANVAS pixels (640x720) — display-space
// conversion happens here only. Canvas-px ↔ mm uses 1.25 mm per canvas
// px, so a 320 px wide box lands at 400 mm (Front/Back), an 88 px box
// lands at 110 mm (hat front), etc. That ratio puts the 40 x 50 cm
// Front/Back print at 50% x 56% of the canvas — comfortably inside the
// shirt body in a typical mockup.
import React, { useEffect, useRef, useState } from 'react'

const CANVAS_W = 640
const CANVAS_H = 720
const DISPLAY_W = 480
const SCALE = DISPLAY_W / CANVAS_W
const DISPLAY_H = CANVAS_H * SCALE

export interface PrintAreaValue { left: number; top: number; width: number; height: number }
// Default 40 x 50 cm box (320 x 400 canvas-px at 1.25 mm/px), centred on
// the canvas.
export const DEFAULT_PRINT_AREA: PrintAreaValue = { left: 160, top: 160, width: 320, height: 400 }

// Per-mockup calibration: admin marks two reference points on the mockup
// and supplies their real-world distance in mm. The editor (and the
// customer Designer) then rescale the mockup IMAGE so its real-world
// features line up with the studio's 1.25 mm/canvas-px ratio. The print
// area itself + every other canvas-coord value stay untouched.
export interface MockupCalibration {
  p1: { x: number; y: number }  // canvas-coord (0..640, 0..720)
  p2: { x: number; y: number }
  mm: number                    // real-world distance between p1 and p2
}

// Studio canvas-coord -> mm ratio (must match canvasPxToMm in the API).
const STUDIO_MM_PER_CANVAS_PX = 1.25

// Pure-function adapter — returns the CSS transform that re-renders a
// natural-object-contain mockup so it represents STUDIO_MM_PER_CANVAS_PX
// mm per canvas-px after scaling. Returns undefined when no calibration
// is set.
//
// Origin: CANVAS CENTRE (50%, 50%) — NOT the calibration midpoint. The
// previous version anchored on the calibration line midpoint, which
// pushed features far from that line off-canvas at high scale factors
// (e.g. shoulder-seam calibration → chest feature ends up below the
// canvas → clamp() snaps the print-area rect back to the edge → "my
// setup shifted on save"). Anchoring on canvas centre keeps the
// commonly-placed print areas (which sit near the canvas centre) put,
// and any features far from centre still shift but rarely past the
// canvas bounds.
//
// Derivation:
//   On the natural mockup, calib.mm of real-world fits inside `dist`
//   canvas-px between the two markers. So the natural mockup is at
//   `mockupMmPerPx = calib.mm / dist` mm per canvas-px.
//   After CSS scale(f), one source canvas-px covers f canvas-px on
//   screen, so the displayed scale is mockupMmPerPx / f. Setting that
//   = STUDIO_MM_PER_CANVAS_PX gives f = mockupMmPerPx / STUDIO_MM_PER_CANVAS_PX.
const CANVAS_CENTRE = { x: CANVAS_W / 2, y: CANVAS_H / 2 }

export function calibrationCss(calib: MockupCalibration | null | undefined): React.CSSProperties | undefined {
  const scale = calibrationScale(calib)
  if (scale == null) return undefined
  return {
    transform: `scale(${scale})`,
    transformOrigin: '50% 50%',
  }
}

// Shared internal — returns just the scale factor. The scale origin is
// always the canvas centre (so forward / inverse mappers below can hard-
// code it).
function calibrationScale(calib: MockupCalibration | null | undefined): number | null {
  if (!calib) return null
  const dx = calib.p2.x - calib.p1.x
  const dy = calib.p2.y - calib.p1.y
  const dist = Math.sqrt(dx * dx + dy * dy)
  if (dist <= 0) return null
  const mockupMmPerPx = calib.mm / dist
  if (!isFinite(mockupMmPerPx) || mockupMmPerPx <= 0) return null
  return mockupMmPerPx / STUDIO_MM_PER_CANVAS_PX
}

// Forward — map a point that lives on the NATURAL mockup to the canvas
// position it appears at once `calib` is applied. Origin is canvas centre.
function applyCalibrationToPoint(p: { x: number; y: number }, calib: MockupCalibration | null | undefined): { x: number; y: number } {
  const scale = calibrationScale(calib)
  if (scale == null) return p
  return {
    x: CANVAS_CENTRE.x + (p.x - CANVAS_CENTRE.x) * scale,
    y: CANVAS_CENTRE.y + (p.y - CANVAS_CENTRE.y) * scale,
  }
}

// Inverse — map a point currently displayed under `calib` BACK to its
// position on the natural mockup.
function unapplyCalibrationToPoint(p: { x: number; y: number }, calib: MockupCalibration | null | undefined): { x: number; y: number } {
  const scale = calibrationScale(calib)
  if (scale == null) return p
  return {
    x: CANVAS_CENTRE.x + (p.x - CANVAS_CENTRE.x) / scale,
    y: CANVAS_CENTRE.y + (p.y - CANVAS_CENTRE.y) / scale,
  }
}

interface Preset { id: string; label: string; mm: string; width: number; height: number }

const PRESETS: Record<string, Preset> = {
  front_back: { id: 'front_back', label: 'Front / Back print',  mm: '400 x 500 mm', width: 320, height: 400 },
  sleeve_l:   { id: 'sleeve_l',   label: 'Sleeve (long)',       mm: '80 x 250 mm',  width:  64, height: 200 },
  sleeve_s:   { id: 'sleeve_s',   label: 'Sleeve (short)',      mm: '80 x 130 mm',  width:  64, height: 104 },
  hat_front:  { id: 'hat_front',  label: 'Hat front',           mm: '110 x 50 mm',  width:  88, height:  40 },
  hat_side:   { id: 'hat_side',   label: 'Hat side / back',     mm: '70 x 35 mm',   width:  56, height:  28 },
  chest:      { id: 'chest',      label: 'Left / right chest',  mm: '90 x 90 mm',   width:  72, height:  72 },
  hem:        { id: 'hem',        label: 'Hem strip',           mm: '200 x 50 mm',  width: 160, height:  40 },
}
const PRESET_LIST: Preset[] = Object.values(PRESETS)

function presetForLocationName(name: string | undefined | null): Preset | null {
  const n = (name ?? '').toLowerCase().trim()
  if (!n) return null
  if (n === 'front' || n === 'back' || n.includes('full chest')) return PRESETS.front_back
  if (n.includes('sleeve')) return n.includes('long') ? PRESETS.sleeve_l : PRESETS.sleeve_s
  if (n.includes('cuff')) return PRESETS.sleeve_s
  if (n.includes('hat') || n.includes('cap')) {
    if (n.includes('side') || n.includes('panel') || (n.includes('back') && (n.includes('hat') || n.includes('cap')))) return PRESETS.hat_side
    return PRESETS.hat_front
  }
  if ((n.includes('chest') || n.includes('pocket')) && (n.includes('left') || n.includes('right'))) return PRESETS.chest
  if (n.includes('hem')) return PRESETS.hem
  return null
}

function clamp(value: PrintAreaValue): PrintAreaValue {
  let { left, top, width, height } = value
  width  = Math.max(20, Math.min(width,  CANVAS_W))
  height = Math.max(20, Math.min(height, CANVAS_H))
  left = Math.max(0, Math.min(left, CANVAS_W - width))
  top  = Math.max(0, Math.min(top,  CANVAS_H - height))
  return { left: Math.round(left), top: Math.round(top), width: Math.round(width), height: Math.round(height) }
}

interface Drag {
  startClientX: number
  startClientY: number
  startValue: PrintAreaValue
  moved: boolean
}

export default function PrintAreaEditor({
  mockupUrl, value, onChange, locationName,
  mockupCalibration, onMockupCalibrationChange,
  onSaveCalibrationCombined, colourSelector,
}: {
  mockupUrl: string | null
  value: PrintAreaValue
  onChange: (next: PrintAreaValue) => void
  locationName?: string | null
  mockupCalibration?: MockupCalibration | null
  onMockupCalibrationChange?: (next: MockupCalibration | null) => void
  // Optional combined writer — when supplied, saveCalibration /
  // clearCalibration use it instead of the (value, calibration)
  // pair to avoid the React batching trap where one update
  // overwrites the other inside Partial<PrintLocation> merges.
  onSaveCalibrationCombined?: (rebased: PrintAreaValue, calib: MockupCalibration | null) => void
  // Slot for the colour picker rendered next to the calibrate button.
  colourSelector?: React.ReactNode
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const toolbarRef = useRef<HTMLDivElement | null>(null)
  const [drag, setDrag] = useState<Drag | null>(null)
  // Toolbar visibility — open after a click ON the print-area rect; close
  // when the admin clicks outside of the rect + toolbar. A drag that moves
  // the rect doesn't open the toolbar (so positioning stays uninterrupted).
  const [toolbarOpen, setToolbarOpen] = useState(false)
  // Calibration mode state. While calibrating the mockup is rendered at its
  // NATURAL object-contain scale (so admin can drop the handles on real
  // features); the saved calibration is applied to subsequent renders.
  const [calibrating, setCalibrating] = useState(false)
  const [calibP1, setCalibP1] = useState<{ x: number; y: number }>({ x: 220, y: 120 })
  const [calibP2, setCalibP2] = useState<{ x: number; y: number }>({ x: 420, y: 120 })
  const [calibMmInput, setCalibMmInput] = useState<string>('460')
  const [calibDragId, setCalibDragId] = useState<'p1' | 'p2' | null>(null)

  const nameLocked = presetForLocationName(locationName ?? null)

  // Auto-correct dimensions to the name-locked standard. Re-runs whenever
  // value.width / height drift away from the preset — covers post-save
  // re-fetches and any other state-replacement path that hands stale
  // dimensions back (the migration-scaled 140 x 158 case). The early-
  // return at the top blocks the otherwise-classic loop.
  useEffect(() => {
    if (!nameLocked) return
    if (value.width === nameLocked.width && value.height === nameLocked.height) return
    const cx = value.left + value.width / 2
    const cy = value.top + value.height / 2
    onChange(clamp({
      left: cx - nameLocked.width / 2,
      top:  cy - nameLocked.height / 2,
      width: nameLocked.width,
      height: nameLocked.height,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameLocked?.id, value.width, value.height])

  // Move drag handler.
  useEffect(() => {
    if (!drag) return
    function onMove(e: MouseEvent) {
      const dx = (e.clientX - drag!.startClientX) / SCALE
      const dy = (e.clientY - drag!.startClientY) / SCALE
      const s = drag!.startValue
      onChange(clamp({ ...s, left: s.left + dx, top: s.top + dy }))
    }
    function onUp() { setDrag(null) }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
  }, [drag, onChange])

  // Calibration crosshair drag handler — tracks cursor in display-px and
  // converts to canvas-coord via /SCALE. Active while `calibDragId` is set.
  useEffect(() => {
    if (!calibDragId) return
    function onMove(e: MouseEvent) {
      const el = containerRef.current; if (!el) return
      const r = el.getBoundingClientRect()
      const cx = Math.max(0, Math.min(CANVAS_W, (e.clientX - r.left) / SCALE))
      const cy = Math.max(0, Math.min(CANVAS_H, (e.clientY - r.top)  / SCALE))
      if (calibDragId === 'p1') setCalibP1({ x: cx, y: cy })
      else                       setCalibP2({ x: cx, y: cy })
    }
    function onUp() { setCalibDragId(null) }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
  }, [calibDragId])

  function startCalibration() {
    if (mockupCalibration) {
      setCalibP1(mockupCalibration.p1); setCalibP2(mockupCalibration.p2)
      setCalibMmInput(String(mockupCalibration.mm))
    } else {
      setCalibP1({ x: 220, y: 120 }); setCalibP2({ x: 420, y: 120 })
      setCalibMmInput('460')
    }
    setCalibrating(true); setToolbarOpen(false)
  }
  // When calibration changes, the mockup re-renders at a different scale.
  // The print-area rect is stored in canvas-coord, so if we leave its
  // position alone the box ends up hovering over a DIFFERENT part of the
  // (now-scaled) shirt — exactly the "my setup shifted on save" bug.
  //
  // Fix: move the rect so its centre stays anchored to the same shirt
  // feature. Walk the rect's centre backwards through the OLD calibration
  // to get the feature's coords on the natural mockup, then forwards
  // through the NEW calibration to get the canvas-coord that displays in
  // the same visual spot under the new transform.
  function rebaseValueForCalibrationChange(
    oldCalib: MockupCalibration | null | undefined,
    newCalib: MockupCalibration | null,
  ): PrintAreaValue {
    const oldCenter = { x: value.left + value.width / 2, y: value.top + value.height / 2 }
    const natural   = unapplyCalibrationToPoint(oldCenter, oldCalib)
    const newCenter = applyCalibrationToPoint(natural, newCalib)
    return clamp({
      ...value,
      left: newCenter.x - value.width  / 2,
      top:  newCenter.y - value.height / 2,
    })
  }
  function saveCalibration() {
    const mm = parseFloat(calibMmInput)
    if (!Number.isFinite(mm) || mm <= 0) { alert('Enter a positive distance in mm.'); return }
    const next: MockupCalibration = { p1: calibP1, p2: calibP2, mm }
    const rebased = rebaseValueForCalibrationChange(mockupCalibration, next)
    // Prefer the combined writer (one parent-state mutation) over the
    // pair below — back-to-back onChange + onMockupCalibrationChange
    // both flow through PrintLocationsEditor.update(id, Partial<>),
    // and React batches them: the second call's `{...l_old, mockupCalibration}`
    // closes over the pre-rebase `l_old` and overwrites the position update,
    // which is exactly the "shift after Apply" the admin keeps reporting.
    if (onSaveCalibrationCombined) {
      onSaveCalibrationCombined(rebased, next)
    } else {
      onChange(rebased)
      onMockupCalibrationChange?.(next)
    }
    setCalibrating(false)
  }
  function cancelCalibration() {
    setCalibrating(false)
  }
  function clearCalibration() {
    const rebased = rebaseValueForCalibrationChange(mockupCalibration, null)
    if (onSaveCalibrationCombined) {
      onSaveCalibrationCombined(rebased, null)
    } else {
      onChange(rebased)
      onMockupCalibrationChange?.(null)
    }
  }

  // Compute the live calibration to apply right now: while calibrating use
  // the in-progress state so the admin sees the calibration update live;
  // otherwise use whatever is saved on the print location.
  const liveCalib: MockupCalibration | null = calibrating
    ? { p1: calibP1, p2: calibP2, mm: parseFloat(calibMmInput) || 1 }
    : (mockupCalibration ?? null)
  // Mockup CSS transform — applied to the IMG only. Disabled during
  // calibration so the admin can drop the handles on the real (un-scaled)
  // mockup features.
  const mockupTransform = calibrating ? undefined : calibrationCss(liveCalib)

  // Close the toolbar when the admin clicks anywhere outside of the rect
  // and the toolbar itself.
  useEffect(() => {
    if (!toolbarOpen) return
    function onDocDown(e: MouseEvent) {
      const t = e.target as Node | null
      if (!t) return
      if (toolbarRef.current?.contains(t)) return
      // The rect's mouse-down handler will handle re-toggling if needed.
      const rectEl = containerRef.current?.querySelector('[data-print-rect="1"]')
      if (rectEl && rectEl.contains(t)) return
      setToolbarOpen(false)
    }
    document.addEventListener('mousedown', onDocDown)
    return () => document.removeEventListener('mousedown', onDocDown)
  }, [toolbarOpen])

  function startDrag(e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation()
    // Open the toolbar immediately on mousedown so a quick click reliably
    // surfaces it — no race against the window mouseup listener attaching.
    // Drag still works in parallel because the drag state is set in the
    // same call; the toolbar simply stays open while the box is moved.
    setToolbarOpen(true)
    setDrag({ startClientX: e.clientX, startClientY: e.clientY, startValue: { ...value }, moved: false })
  }

  function applyPreset(id: string) {
    if (nameLocked) return
    const p = PRESET_LIST.find((x) => x.id === id)
    if (!p) return
    const cx = value.left + value.width / 2
    const cy = value.top + value.height / 2
    onChange(clamp({ left: cx - p.width / 2, top: cy - p.height / 2, width: p.width, height: p.height }))
  }

  // Reset to a known-good starting box. If the location name locks the size
  // we use that preset (so reset still respects the lock); otherwise we use
  // the 40x50 DEFAULT_PRINT_AREA. Either way, the box ends up centered on
  // the canvas so it's easy to find again.
  function resetArea() {
    const target = nameLocked
      ? { width: nameLocked.width, height: nameLocked.height }
      : { width: DEFAULT_PRINT_AREA.width, height: DEFAULT_PRINT_AREA.height }
    onChange(clamp({
      left: (CANVAS_W - target.width) / 2,
      top:  (CANVAS_H - target.height) / 2,
      width: target.width,
      height: target.height,
    }))
  }

  const display = {
    left: value.left * SCALE,
    top: value.top * SCALE,
    width: value.width * SCALE,
    height: value.height * SCALE,
  }
  const activePreset = nameLocked ?? PRESET_LIST.find((p) => p.width === value.width && p.height === value.height)

  // Position the toolbar above the print-area rect when it fits; otherwise
  // tuck it just inside the top of the canvas so it stays on-screen.
  const toolbarTop = Math.max(0, display.top - 44)

  // Friendly readout of the live calibration ratio so the admin sees the
  // mm-per-canvas-px the editor is actually applying.
  const calibRatio = mockupCalibration
    ? (() => {
        const dx = mockupCalibration.p2.x - mockupCalibration.p1.x
        const dy = mockupCalibration.p2.y - mockupCalibration.p1.y
        const dist = Math.sqrt(dx * dx + dy * dy)
        return dist > 0 ? mockupCalibration.mm / dist : null
      })()
    : null

  return (
    <div>
      {/* Mockup calibration row — sits above the editor so the admin
          knows up-front whether their placement is going to be accurate
          or approximate. */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] font-bold">
          {mockupCalibration ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 normal-case tracking-normal text-[11px]">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Calibrated: {mockupCalibration.mm.toFixed(0)} mm between markers
              {calibRatio && <span className="text-emerald-700 font-mono">({calibRatio.toFixed(2)} mm/px)</span>}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 normal-case tracking-normal text-[11px]">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Uncalibrated mockup — placement is approximate
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {colourSelector}
          <button type="button" onClick={startCalibration} disabled={!mockupUrl || calibrating}
            className="btn-ghost !text-[11px] !py-1 !px-2 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Click two reference points on the mockup and supply their real-world distance">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="6" cy="6"   r="2"/><circle cx="18" cy="18" r="2"/><line x1="7.5" y1="7.5" x2="16.5" y2="16.5"/>
            </svg>
            {mockupCalibration ? 'Re-calibrate' : 'Calibrate mockup'}
          </button>
          {mockupCalibration && !calibrating && (
            <button type="button" onClick={clearCalibration}
              className="text-[11px] font-semibold uppercase tracking-wide text-ink-3 hover:text-red-600 underline underline-offset-2"
              title="Clear the calibration and fall back to the un-scaled mockup">Clear</button>
          )}
        </div>
      </div>

      {/* Persist reminder — Apply / Re-calibrate / Clear only update the
          working draft. The actual DB write happens via the page-level
          Save Print Locations button in GarmentDetail. Without this hint
          admins (rightly!) assume the toolbar's Save button persists, and
          a refresh wipes their calibration. */}
      <p className="text-[10px] text-ink-3 italic mb-2 -mt-1">
        Calibration + box position live in the working draft. Click <strong className="not-italic font-semibold text-ink-2">Save Print Locations</strong> at the top of this card to persist.
      </p>

      <div
        ref={containerRef}
        className="relative bg-surface border border-border rounded overflow-hidden select-none mx-auto"
        style={{ width: DISPLAY_W, height: DISPLAY_H, maxWidth: '100%' }}
      >
        {mockupUrl ? (
          <img src={mockupUrl} alt="Mockup"
               className="absolute inset-0 w-full h-full object-contain pointer-events-none"
               draggable={false}
               style={mockupTransform} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-ink-3 pointer-events-none">Upload a mockup to position the print area against it</div>
        )}

        {/* Calibration overlay — two draggable crosshairs + a floating mm
            input. Only rendered while admin is in calibrate mode so the
            print-area workflow stays uncluttered the rest of the time. */}
        {calibrating && (
          <>
            {/* Line connecting the two points so the admin sees the chord
                they're measuring as a single visual. */}
            <svg className="absolute inset-0 pointer-events-none" style={{ width: DISPLAY_W, height: DISPLAY_H }}>
              <line
                x1={calibP1.x * SCALE} y1={calibP1.y * SCALE}
                x2={calibP2.x * SCALE} y2={calibP2.y * SCALE}
                stroke="#B89232" strokeWidth={2} strokeDasharray="6 4" />
            </svg>
            {(['p1','p2'] as const).map((id) => {
              const p = id === 'p1' ? calibP1 : calibP2
              return (
                <div key={id}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setCalibDragId(id) }}
                  className="absolute z-30 flex items-center justify-center cursor-move"
                  style={{
                    left: p.x * SCALE - 12,
                    top:  p.y * SCALE - 12,
                    width: 24, height: 24,
                  }}>
                  <div className="w-6 h-6 rounded-full bg-white border-2 border-gold-dark shadow flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold-dark" />
                  </div>
                  <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider bg-gold-dark text-white px-1.5 py-0.5 rounded">
                    {id === 'p1' ? 'A' : 'B'}
                  </span>
                </div>
              )
            })}
            {/* Floating distance input — same panel pattern as the size
                toolbar above. */}
            <div className="absolute z-40 bg-white border border-border rounded shadow-lg px-3 py-2 flex items-center gap-2"
                 style={{
                   left: Math.max(8, Math.min(((calibP1.x + calibP2.x) / 2) * SCALE - 110, DISPLAY_W - 230)),
                   top:  Math.max(8, ((calibP1.y + calibP2.y) / 2) * SCALE + 22),
                 }}
                 onMouseDown={(e) => e.stopPropagation()}>
              <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 shrink-0">A → B</span>
              <input type="number" min={1} max={2000} step={1}
                value={calibMmInput}
                onChange={(e) => setCalibMmInput(e.target.value)}
                className="input !text-xs !w-20 !py-1"
                title="Real-world distance between the two crosshairs, in millimetres" />
              <span className="text-[11px] text-ink-3 font-mono">mm</span>
              <button onClick={saveCalibration}
                title="Applies the calibration to the working draft. To persist it, click Save Print Locations at the top of this card."
                className="text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded bg-ink text-white hover:bg-ink-2 transition-colors">Apply</button>
              <button onClick={cancelCalibration}
                className="text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded border border-border text-ink-3 hover:text-ink hover:border-gold transition-colors">Cancel</button>
            </div>
          </>
        )}

        {/* Print area rectangle — position-only, click toggles the toolbar. */}
        <div
          data-print-rect="1"
          className="absolute border-2 border-dashed border-gold bg-gold/10 cursor-move"
          style={display}
          onMouseDown={startDrag}
        >
          <div className={'absolute -top-5 left-0 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded shadow whitespace-nowrap transition-opacity ' +
            (toolbarOpen ? 'opacity-0 pointer-events-none' : 'opacity-100 text-gold-dark bg-white')}>
            {activePreset ? activePreset.mm : 'Print area'} - click to size
          </div>
        </div>

        {/* Floating size toolbar — appears on rectangle click. */}
        {toolbarOpen && (
          <div
            ref={toolbarRef}
            className="absolute z-20 bg-white border border-border rounded shadow-lg px-2 py-1.5 flex items-center gap-2 text-xs"
            style={{
              left: Math.max(8, Math.min(display.left, DISPLAY_W - 320)),
              top: toolbarTop,
            }}
            onMouseDown={(e) => e.stopPropagation()}>
            <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 shrink-0">Size</span>
            {nameLocked ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-gold/15 text-gold-dark text-[11px] font-semibold whitespace-nowrap">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                {nameLocked.label} - {nameLocked.mm}
              </span>
            ) : (
              <>
                <select
                  value={activePreset?.id ?? ''}
                  onChange={(e) => applyPreset(e.target.value)}
                  className="input !text-xs !w-auto !py-1">
                  {!activePreset && <option value="">- Custom (legacy) -</option>}
                  {PRESET_LIST.map((p) => (
                    <option key={p.id} value={p.id}>{p.label} - {p.mm}</option>
                  ))}
                </select>
                {activePreset && <span className="text-[11px] text-ink-3 font-mono whitespace-nowrap">{activePreset.mm}</span>}
              </>
            )}
            <button
              onClick={() => setToolbarOpen(false)}
              className="ml-1 text-ink-3 hover:text-ink text-base leading-none w-5 h-5 flex items-center justify-center"
              title="Close size toolbar"
              aria-label="Close size toolbar">×</button>
          </div>
        )}
      </div>

      {/* Industry-standard preset chips + reset. Sits above the dimensions
          grid so the admin can resize the box without having to click into
          the canvas and find the floating toolbar. When the location name
          locks the size (Front/Back/Sleeve/Hat/etc.), the matching chip is
          highlighted and the others are disabled — reset still works and
          re-centers the box. */}
      <div className="mt-4 border border-border rounded-md bg-surface/40 px-3 py-2.5">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 flex items-center gap-1.5">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M3 9h18"/>
            </svg>
            Industry standards
            {nameLocked && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gold/15 text-gold-dark text-[9px] font-bold normal-case tracking-normal">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Locked by location name
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={resetArea}
            className="btn-ghost !text-[11px] !py-1 !px-2 flex items-center gap-1"
            title="Reset the print area to a centered default"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
            </svg>
            Reset area
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PRESET_LIST.map((p) => {
            const isActive = activePreset?.id === p.id
            const isDisabled = !!nameLocked && !isActive
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => applyPreset(p.id)}
                disabled={isDisabled}
                title={isDisabled ? 'Locked by location name — change the location name to switch sizes' : `${p.label} - ${p.mm}`}
                className={
                  'inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-semibold border transition-colors whitespace-nowrap ' +
                  (isActive
                    ? 'bg-gold/15 border-gold/60 text-gold-dark'
                    : isDisabled
                      ? 'bg-white/40 border-border text-ink-3/60 cursor-not-allowed'
                      : 'bg-white border-border text-ink-2 hover:border-gold/60 hover:text-gold-dark')
                }>
                <span>{p.label}</span>
                <span className="font-mono text-[10px] text-ink-3">{p.mm}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Position-only numeric inputs. Width + height are always read-only
          now — sizing is fixed by the standard. */}
      <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">Left</div>
          <input type="number" min={0} max={CANVAS_W} value={value.left}
            onChange={(e) => onChange(clamp({ ...value, left: parseInt(e.target.value) || 0 }))}
            className="input !text-xs" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">Top</div>
          <input type="number" min={0} max={CANVAS_H} value={value.top}
            onChange={(e) => onChange(clamp({ ...value, top: parseInt(e.target.value) || 0 }))}
            className="input !text-xs" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">Width</div>
          <input type="number" value={value.width} disabled
            className="input !text-xs bg-surface cursor-not-allowed" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">Height</div>
          <input type="number" value={value.height} disabled
            className="input !text-xs bg-surface cursor-not-allowed" />
        </div>
      </div>
      <p className="text-[11px] text-ink-3 mt-2">
        {nameLocked
          ? <>Size is fixed at <strong className="text-ink-2">{nameLocked.label} ({nameLocked.mm})</strong> for this location name. Drag the box to position; click it to open the size toolbar.</>
          : <>Click the box to open the <strong className="text-ink-2">industry-standards size toolbar</strong>. Drag the box to position it on the mockup.</>}
      </p>
    </div>
  )
}
